import { type Manifest } from '@doxyme/extensions-graphql-types';
import { _getLogger } from '@doxyme/analytics';
import { bridge } from '../instances';
import { rebaseManifestUrls } from '../../../utils/rebaseManifestUrls';
import { generateSideloadId } from './generateSideloadId';
import type { ProcessSideloadOptions, ProcessSideloadResult } from './types';

const logger = _getLogger('processSideloadExtension', {
  component: 'extensions',
});

/**
 * Processes a sideload operation: generates ID, rebases URLs, removes existing instance, and adds to bridge.
 *
 * This is the core sideloading logic shared between URL parameter sideloading
 * and console-based sideloading.
 *
 * @param options - The sideload options containing baseUrl and manifest
 * @returns The sideloaded extension's ID and name
 * @throws Error if the sideload operation fails
 */
export const processSideloadExtension = (
  options: ProcessSideloadOptions,
): ProcessSideloadResult => {
  const { baseUrl, manifest } = options;

  const sideloadId = manifest.id ?? generateSideloadId();
  const manifestWithId: Manifest = {
    ...manifest,
    id: sideloadId,
  };

  const rebasedManifest = rebaseManifestUrls(manifestWithId, baseUrl);

  const existingInstanceId = bridge.findInstanceByName(manifest.name);
  if (existingInstanceId) {
    bridge.removeInstance(existingInstanceId);
    logger.info('[processSideloadExtension] Removed existing instance', {
      id: existingInstanceId,
      name: manifest.name,
    });
  }

  bridge.addInstance(rebasedManifest, { source: 'sideload' });
  logger.info('[processSideloadExtension] Sideloaded extension', {
    id: rebasedManifest.id,
    name: rebasedManifest.name,
    baseUrl,
  });

  return { id: rebasedManifest.id, name: rebasedManifest.name };
};
